import { useState, useMemo, useEffect } from 'react';
import { v4 as uuid } from 'uuid';

import { useDefaultAppContexts } from 'hb-react/frontoffice/contexts/withDefaultAppContexts';
import { isGSFileScanned } from 'hb-react/shared/hooks/useGlobalStorageV2/utils/isGSFileScanned';
import { useSubscribeToAblyChannel } from 'hb-react/shared/hooks/useSubscribeToAblyChannel';
import useVirusScanningEnabled from 'hb-react/shared/hooks/useVirusScanningEnabled';
import * as errorReporting from 'hb-react/shared/utils/errorReporting';
import { formatFileSize } from 'hb-react/shared/utils/files/fileSize';

import { FILE_TOO_LARGE_ERROR, FILE_UPLOAD_ERROR } from '../constants';
import { OwnerType } from '../types';
import type {
  GetUpdateFile,
  GlobalStorageFile,
  GetUpdateStoredFile,
  GlobalStorageFileWithError,
  MaybeGlobalStorageFile,
  UseGlobalStorageFileParams,
  UseGlobalStorageFileReturnType,
} from '../types';
import {
  fileMatchesIdentifiers,
  getAblyVirusScanChannel,
  getAblyTokenType,
  presignAndUpload,
  getAblyVirusScanMessageHandler,
  areFilesReadyToSubmit,
} from '../utils';

const getUpdateStoredFile: GetUpdateStoredFile =
  ({ setStoredFile }) =>
  (identifiers, fileOrFunc) =>
    setStoredFile((prevFile) => {
      // In case asynchronous updates arrive out of order, don't apply the
      // update if the file no longer exists or doesn't match the identifiers
      // anymore.
      if (!prevFile || !fileMatchesIdentifiers(prevFile, identifiers)) {
        return prevFile;
      }

      // The file is already in an error state, maintain its state to make debugging easier
      if (prevFile.error) {
        return prevFile;
      }

      return typeof fileOrFunc === 'function'
        ? fileOrFunc(prevFile)
        : { ...prevFile, ...fileOrFunc };
    });

const getUpdateFile: GetUpdateFile =
  ({
    setStoredFile,
    setIsProcessing,
    abortController,
    updateStoredFile,
    virusScanningEnabled,
    ownerType,
    currentNetworkId,
    config,
    subscribeToAblyChannel,
  }) =>
  async ({ file, key }) => {
    // Cancel any pending request associated with the previous file
    abortController?.abort();

    // Move to the processing state
    setIsProcessing(true);

    if (!file) {
      setStoredFile(null);

      return null;
    }

    const newFile: WithRequired<GlobalStorageFile, 'file'> | null = {
      file,
      key: key ?? uuid(),
      uploadPercentage: 0,
      abortController: new AbortController(),
    };

    setStoredFile(newFile);

    if (config?.maxFileSize && file.size > config.maxFileSize) {
      const error = FILE_TOO_LARGE_ERROR.i__({
        size: formatFileSize(config.maxFileSize),
      });

      updateStoredFile(
        { key: newFile.key },
        { error, isRemoved: config?.markFileAsRemovedOnError },
      );

      return null;
    }

    if (virusScanningEnabled) {
      await subscribeToAblyChannel();
    }

    try {
      return await presignAndUpload({
        controller: newFile.abortController,
        file,
        inBO: ownerType === OwnerType.ADMIN,
        key: newFile.key,
        networkId: currentNetworkId,
        updateStoredFile,
        virusScanningEnabled,
      });
    } catch (error) {
      errorReporting.captureException(error, {
        tags: { tribe: 'evolution-tribe' },
      });
      updateStoredFile(
        { key: newFile.key },
        {
          error: FILE_UPLOAD_ERROR.i__({
            name: newFile.file?.name,
          }),
          isRemoved: config?.markFileAsRemovedOnError,
        },
      );

      return null;
    }
  };

export const useGlobalStorageFile = ({
  initialFile,
  onSuccess,
  onError,
  config,
}: UseGlobalStorageFileParams = {}): UseGlobalStorageFileReturnType => {
  const { currentAdmin, currentUser, currentNetworkId } =
    useDefaultAppContexts();
  const virusScanningEnabled = useVirusScanningEnabled();

  const [storedFile, setStoredFile] =
    useState<MaybeGlobalStorageFile>(initialFile);
  const [isProcessing, setIsProcessing] = useState<boolean>(false);

  // Check if the file has finished processing
  useEffect(() => {
    if (!storedFile) {
      setIsProcessing(false);

      return;
    }

    if (storedFile.error) {
      setIsProcessing(false);

      return;
    }

    const readyToSubmit = areFilesReadyToSubmit(
      [storedFile],
      virusScanningEnabled,
    );

    setIsProcessing(!readyToSubmit);
  }, [storedFile, virusScanningEnabled]);

  // File has been processed, call the callbacks if they exist
  useEffect(() => {
    // Don't call the callbacks if the file hasn't changed
    if (
      storedFile === undefined ||
      storedFile === initialFile ||
      isProcessing
    ) {
      return;
    }

    if (storedFile?.error) {
      onError?.(storedFile as GlobalStorageFileWithError);
    } else {
      onSuccess?.(storedFile);
    }
  }, [initialFile, isProcessing, onError, onSuccess, storedFile]);

  const updateStoredFile = useMemo(
    () => getUpdateStoredFile({ setStoredFile }),
    [],
  );

  const ablyVirusScanMessageHandler = useMemo(
    () =>
      getAblyVirusScanMessageHandler(
        updateStoredFile,
        config?.markFileAsRemovedOnError,
      ),
    [config?.markFileAsRemovedOnError, updateStoredFile],
  );

  const ownerType = currentAdmin ? OwnerType.ADMIN : OwnerType.USER;
  const ownerId = currentAdmin?.id ?? currentUser?.id;

  const { subscribe, unsubscribe } = useSubscribeToAblyChannel({
    autoConnect: false,
    channel: getAblyVirusScanChannel(ownerType, ownerId),
    enabled: virusScanningEnabled,
    onMessage: ablyVirusScanMessageHandler,
    onResubscribedAfterInactivity: null,
    tokenType: getAblyTokenType(ownerType),
  });

  const updateFile = useMemo(
    () =>
      getUpdateFile({
        setStoredFile,
        setIsProcessing,
        abortController: storedFile?.abortController,
        updateStoredFile,
        virusScanningEnabled,
        ownerType,
        currentNetworkId,
        config,
        subscribeToAblyChannel: subscribe,
      }),
    [
      storedFile?.abortController,
      updateStoredFile,
      virusScanningEnabled,
      ownerType,
      currentNetworkId,
      config,
      subscribe,
    ],
  );

  useEffect(() => {
    if (!storedFile?.virusScanStatus) {
      return;
    }

    // Unsubscribe from the ably channel once the file has been scanned or errored out
    if (isGSFileScanned(storedFile) || storedFile.error) {
      unsubscribe();
    }
  }, [storedFile, unsubscribe]);

  return {
    file: storedFile,
    updateFile,
    isProcessing,
  };
};
